import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead, clean } from "../../utils/sqlClient";
import ArchitecturalRoomsPanel from "./ArchitecturalRoomsPanel";

// Deliberate variant of utils/format.js fmtDate: includes the time of day
// (revision timestamps), rendered in the viewer's local timezone.
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

const COMMON_LABELS = ["ROM", "Schematic", "Design Development", "Construction Documents", "Final Bid", "Value Engineering"];

const SCOPE_DELIVERY_OPTIONS = [
  { value: "Box",     label: "Box",     desc: "Architectural only" },
  { value: "Box+",    label: "Box+",    desc: "Arch + MEP + Process" },
  { value: "Full CM", label: "Full CM", desc: "All scopes + GC coordination" },
];

const SCOPE_DELIVERY_COLORS = {
  "Box":     { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  "Box+":    { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  "Full CM": { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" },
};

function ScopeDeliveryBadge({ value }) {
  if (!value) return null;
  const c = SCOPE_DELIVERY_COLORS[value] || {};
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 100,
      background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {value}
    </span>
  );
}

export default function Revisions({ projectId }) {
  const { accounts } = useMsal();
  const user = accounts[0];

  const [revisions, setRevisions]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showNew, setShowNew]       = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [savingDelivery, setSavingDelivery]   = useState(false);

  useEffect(() => {
    sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`)
      .then(rows => setRevisions(rows))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  const updateScopeDelivery = async (revisionId, value) => {
    setSavingDelivery(true);
    try {
      await sqlRead(`EXEC sp_SetScopeDeliveryMethod @payload='${clean(JSON.stringify({ revision_id: revisionId, scope_delivery_method: value || null }))}'`);
      setRevisions(prev => prev.map(r => r.RevisionID === revisionId ? { ...r, ScopeDeliveryMethod: value || null } : r));
      setEditingDelivery(false);
    } catch(e) { alert(e.message); }
    setSavingDelivery(false);
  };

  if (loading) return <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading revisions…</div>;

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div className="panel-title">Estimate Revisions</div>
          <div className="panel-sub">Each revision is a named snapshot of the BOD checklist and estimate. The active revision is editable — creating a new one locks the current.</div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          style={{ padding: "8px 14px", fontSize: 12, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}
        >
          + New revision
        </button>
      </div>

      {revisions.length === 0 ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>📐</div>
          <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>No revisions yet</div>
          <div style={{ marginBottom: 16 }}>Create your first revision to start the BOD checklist and estimate.</div>
          <button
            onClick={() => setShowNew(true)}
            style={{ padding: "9px 20px", fontSize: 13, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}
          >
            Create Rev 0 →
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {revisions.map(rev => {
            const isActive = !rev.IsLocked;
            return (
              <div key={rev.RevisionID} style={{
                border: "1px solid var(--cs-border)",
                borderLeft: `3px solid ${isActive ? "var(--cs-blue)" : "#d1d5db"}`,
                borderRadius: "var(--radius)",
                background: "var(--surface)",
                padding: "14px 16px",
                opacity: isActive ? 1 : 0.85,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
                    Rev {rev.RevisionNumber}: {rev.RevisionLabel}
                  </div>
                  {isActive ? (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "var(--cs-blue-lt)", color: "var(--cs-blue)" }}>Active</span>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "#f3f4f6", color: "var(--text3)" }}>🔒 Locked</span>
                  )}
                  {/* Scope delivery badge — clickable on active revision */}
                  {rev.ScopeDeliveryMethod ? (
                    isActive ? (
                      <button onClick={() => setEditingDelivery(editingDelivery === rev.RevisionID ? false : rev.RevisionID)}
                        title="Change scope delivery type"
                        style={{ all: "unset", cursor: "pointer" }}>
                        <ScopeDeliveryBadge value={rev.ScopeDeliveryMethod} />
                      </button>
                    ) : (
                      <ScopeDeliveryBadge value={rev.ScopeDeliveryMethod} />
                    )
                  ) : isActive ? (
                    <button onClick={() => setEditingDelivery(editingDelivery === rev.RevisionID ? false : rev.RevisionID)}
                      style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "var(--tint-amber)", color: "#d97706", border: "1px solid #fde68a", cursor: "pointer" }}>
                      Set scope type
                    </button>
                  ) : null}
                </div>

                {/* Inline scope delivery editor (active revision only) */}
                {editingDelivery === rev.RevisionID && (
                  <div style={{ marginBottom: 10, padding: "10px 12px", background: "var(--surface2)", borderRadius: "var(--radius)", border: "1px solid var(--cs-border)" }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Scope delivery type</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {SCOPE_DELIVERY_OPTIONS.map(opt => {
                        const selected = rev.ScopeDeliveryMethod === opt.value;
                        const c = SCOPE_DELIVERY_COLORS[opt.value];
                        return (
                          <button key={opt.value}
                            disabled={savingDelivery}
                            onClick={() => updateScopeDelivery(rev.RevisionID, opt.value)}
                            style={{ padding: "6px 12px", borderRadius: "var(--radius)", border: `1px solid ${selected ? c.border : "var(--cs-border)"}`,
                              background: selected ? c.bg : "var(--surface)", color: selected ? c.color : "var(--text2)",
                              fontSize: 12, fontWeight: selected ? 600 : 400, cursor: "pointer", textAlign: "left" }}>
                            <div style={{ fontWeight: 600 }}>{opt.label}</div>
                            <div style={{ fontSize: 10, opacity: 0.8 }}>{opt.desc}</div>
                          </button>
                        );
                      })}
                      {rev.ScopeDeliveryMethod && (
                        <button disabled={savingDelivery} onClick={() => updateScopeDelivery(rev.RevisionID, "")}
                          style={{ padding: "6px 12px", borderRadius: "var(--radius)", border: "1px solid var(--cs-border)",
                            background: "var(--surface)", color: "var(--text3)", fontSize: 11, cursor: "pointer" }}>
                          Clear
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 8 }}>
                      Changing this updates the revision record. To re-apply RACI defaults, use the Scope & RACI panel.
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 20, fontSize: 12, color: "var(--text3)" }}>
                  <span>Created {fmtDate(rev.CreatedAt)}{rev.CreatedBy ? ` by ${rev.CreatedBy}` : ""}</span>
                  {rev.IsLocked && rev.LockedAt && (
                    <span>Locked {fmtDate(rev.LockedAt)}{rev.LockedBy ? ` by ${rev.LockedBy}` : ""}</span>
                  )}
                  {rev.CopiedFromRevisionID && (
                    <span>Copied from Rev {revisions.find(r => r.RevisionID === rev.CopiedFromRevisionID)?.RevisionNumber ?? rev.CopiedFromRevisionID}</span>
                  )}
                </div>

                {/* The legacy per-revision line-item panel (Estimate_LineItems,
                    db/07) was retired 2026-09-12 (P3-C): it was a second line
                    model the Estimate never priced. Lines — and their carried
                    pricing basis — live on the Estimate tab. */}

                {rev.ScopeDeliveryMethod === "Box" && (
                  <ArchitecturalRoomsPanel revisionId={rev.RevisionID} isLocked={rev.IsLocked} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New revision modal */}
      {showNew && (
        <NewRevisionModal
          projectId={projectId}
          revisions={revisions}
          savedBy={user?.name}
          onSaved={(rev) => {
            setRevisions(prev => [rev, ...prev.map(r => ({ ...r, IsLocked: true }))]);
            setShowNew(false);
          }}
          onCancel={() => setShowNew(false)}
        />
      )}
    </div>
  );
}

function NewRevisionModal({ projectId, revisions, savedBy, onSaved, onCancel }) {
  const [label, setLabel]                     = useState("");
  const [startMode, setStartMode]             = useState("copy"); // 'copy' | 'blank'
  const [copyFromId, setCopyFromId]           = useState(revisions.find(r => !r.IsLocked)?.RevisionID || revisions[0]?.RevisionID || "");
  const [scopeDeliveryMethod, setScopeDM]     = useState(revisions.find(r => !r.IsLocked)?.ScopeDeliveryMethod || "");
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState(null);

  // When the copy-from revision changes, inherit its scope delivery method as a default
  const handleCopyFromChange = (e) => {
    const id = Number(e.target.value);
    setCopyFromId(id);
    const src = revisions.find(r => r.RevisionID === id);
    if (src?.ScopeDeliveryMethod) setScopeDM(src.ScopeDeliveryMethod);
  };

  const save = async () => {
    if (!label.trim()) { setError("Label is required."); return; }
    setSaving(true); setError(null);
    try {
      const copyParam = startMode === "copy" && copyFromId ? copyFromId : "NULL";
      const rows = await sqlRead(`EXEC sp_CreateRevision
        @project_id=${projectId},
        @revision_label='${clean(label)}',
        @created_by='${clean(savedBy || "")}',
        @copy_from_revision_id=${copyParam}${scopeDeliveryMethod ? `,@scope_delivery_method='${clean(scopeDeliveryMethod)}'` : ""}`);
      if (rows[0]?.RevisionID) {
        onSaved({
          RevisionID:             rows[0].RevisionID,
          RevisionNumber:         rows[0].RevisionNumber,
          RevisionLabel:          label,
          IsLocked:               false,
          CreatedAt:              new Date().toISOString(),
          CreatedBy:              savedBy,
          CopiedFromRevisionID:   startMode === "copy" ? copyFromId : null,
          ScopeDeliveryMethod:    rows[0].ScopeDeliveryMethod || null,
        });
      }
    } catch(e) { setError(e.message); }
    setSaving(false);
  };

  const inputStyle = { height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12, color: "var(--text)", width: "100%", outline: "none", boxSizing: "border-box" };
  const sectionLabel = { fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cs-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>New revision</div>
          <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text3)" }}>✕</button>
        </div>
        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Label */}
          <div>
            <label style={sectionLabel}>Revision label</label>
            <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Schematic, Design Development, Final Bid…" autoFocus style={inputStyle} />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {COMMON_LABELS.map(l => (
                <button key={l} onClick={() => setLabel(l)}
                  style={{ fontSize: 11, padding: "3px 9px", borderRadius: 100, background: label === l ? "var(--cs-blue-lt)" : "#f3f4f6", color: label === l ? "var(--cs-blue)" : "var(--text3)", border: "1px solid transparent", cursor: "pointer" }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Scope delivery type */}
          <div>
            <label style={sectionLabel}>Scope delivery type</label>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              {SCOPE_DELIVERY_OPTIONS.map(opt => {
                const selected = scopeDeliveryMethod === opt.value;
                const c = SCOPE_DELIVERY_COLORS[opt.value];
                return (
                  <button key={opt.value} onClick={() => setScopeDM(selected ? "" : opt.value)}
                    style={{ flex: 1, padding: "7px 10px", borderRadius: "var(--radius)",
                      border: `1px solid ${selected ? c.border : "var(--cs-border)"}`,
                      background: selected ? c.bg : "var(--surface)",
                      color: selected ? c.color : "var(--text2)",
                      fontSize: 12, fontWeight: selected ? 600 : 400, cursor: "pointer", textAlign: "center" }}>
                    <div style={{ fontWeight: 600 }}>{opt.label}</div>
                    <div style={{ fontSize: 10, opacity: 0.75 }}>{opt.desc}</div>
                  </button>
                );
              })}
            </div>
            {!scopeDeliveryMethod && (
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 5 }}>
                Select the scope type to pre-seed the Scope & RACI. Can be set later.
              </div>
            )}
          </div>

          {/* Starting point */}
          <div>
            <label style={sectionLabel}>Starting point</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
              {revisions.length > 0 && (
                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13 }}>
                  <input type="radio" checked={startMode === "copy"} onChange={() => setStartMode("copy")} style={{ accentColor: "var(--cs-navy)" }} />
                  <div>
                    <div style={{ fontWeight: 500, color: "var(--text)" }}>Copy from existing revision</div>
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>Carry forward all BOD items and estimate line items</div>
                  </div>
                </label>
              )}
              {startMode === "copy" && revisions.length > 0 && (
                <select value={copyFromId} onChange={handleCopyFromChange}
                  style={{ ...inputStyle, marginLeft: 26 }}>
                  {revisions.map(r => <option key={r.RevisionID} value={r.RevisionID}>Rev {r.RevisionNumber}: {r.RevisionLabel}</option>)}
                </select>
              )}
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13 }}>
                <input type="radio" checked={startMode === "blank"} onChange={() => setStartMode("blank")} style={{ accentColor: "var(--cs-navy)" }} />
                <div>
                  <div style={{ fontWeight: 500, color: "var(--text)" }}>Start blank</div>
                  <div style={{ fontSize: 11, color: "var(--text3)" }}>All checklist items load unchecked, estimate is empty</div>
                </div>
              </label>
            </div>
          </div>

          {revisions.length > 0 && (
            <div style={{ fontSize: 12, color: "#d97706", padding: "8px 12px", background: "var(--tint-amber)", borderRadius: "var(--radius)", border: "1px solid rgba(180,120,0,0.2)" }}>
              ⚠️ Creating this revision will lock the current active revision. This cannot be undone.
            </div>
          )}

          {error && <div style={{ fontSize: 13, color: "var(--red)", padding: "8px 12px", background: "var(--red-lt)", borderRadius: "var(--radius)" }}>{error}</div>}
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--cs-border)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", fontSize: 13, background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer", color: "var(--text2)" }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: "8px 20px", fontSize: 13, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}>
            {saving ? "Creating…" : "Create revision →"}
          </button>
        </div>
      </div>
    </div>
  );
}
